import type { UASnapshot } from '@/server/lib/ua-snapshot'
import type { PerformanceTier } from '@/services/cms'

export type DeviceCapabilityTier = 'unknown' | 'constrained' | 'standard' | 'highEnd'

const GB = 1024

export function resolveCapabilityTier(memoryMb: number | undefined): DeviceCapabilityTier {
  if (memoryMb === undefined) return 'unknown'
  if (memoryMb < 4 * GB) return 'constrained'
  if (memoryMb < 8 * GB) return 'standard'
  return 'highEnd'
}

/** Alias of PerformanceTier from services/cms — used throughout gating logic. */
export type ContentTier = PerformanceTier

export function normalizeContentTier(raw: ContentTier | undefined): ContentTier {
  return raw ?? 'UNKNOWN'
}

/**
 * Internal strength scale.
 * - Normal: no restriction.
 * - Label: show alert overlay label, no confirm dialog (Phase 1 max).
 * - LabelDeprioritize: reserved for future phases; not used in Phase 1.
 */
export type GatingLevel = 'Normal' | 'Label' | 'LabelDeprioritize'

export type GatingLevelInput = {
  sceneType?: string
  device: UASnapshot['device']
  capabilityTier: DeviceCapabilityTier
  contentTier: ContentTier
  mobileBrowserGatingEnabled?: boolean
}

function resolveMobileGatingLevel(
  capabilityTier: DeviceCapabilityTier,
  contentTier: ContentTier,
): GatingLevel {
  if (contentTier === 'TIER_2') {
    return capabilityTier === 'highEnd' ? 'Normal' : 'Label'
  }

  if (contentTier === 'TIER_3') return 'Label'

  return 'Normal'
}

export function resolveGatingLevel({
  sceneType,
  device,
  capabilityTier,
  contentTier,
  mobileBrowserGatingEnabled,
}: GatingLevelInput): GatingLevel {
  if (sceneType !== 'standalone') return 'Normal'

  if (device === 'desktop_browser' || device === 'vr_browser') return 'Normal'

  if (device === 'mobile_browser' && !mobileBrowserGatingEnabled) return 'Normal'

  if (contentTier === 'TIER_1') return 'Normal'
  if (contentTier === 'UNKNOWN') return 'Normal'

  // mobile_app: capability tier sourced from window.ViverseNativeDeviceCapability.
  // mobile_browser: capability tier sourced from device-capability-gate browser detection.
  if (device === 'mobile_app' || device === 'mobile_browser') {
    return resolveMobileGatingLevel(capabilityTier, contentTier)
  }

  return 'Normal'
}

export type ContentDisplayState = 'Normal' | 'Label'

export function resolveContentDisplayState(input: GatingLevelInput): ContentDisplayState {
  const level = resolveGatingLevel(input)
  return level === 'Normal' ? 'Normal' : 'Label'
}
